import { useMemo, useState, useEffect } from 'react';
import { useSAPDataStore } from '../store/SAPDataStore';

export interface SuggestionItem {
  value: string;
  type: 'machine' | 'line';
  label: string;
  subtitle?: string;
  id?: string;
}

/**
 * Custom hook for debouncing a value
 */
const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook that provides auto-suggestions based on SAP data (machines and lines only)
 */
export const useSAPSuggestions = (query: string, maxResults: number = 10): SuggestionItem[] => {
  const { machines, lines } = useSAPDataStore();
  
  // Debounce the search query by 300ms to prevent freezing on each keystroke
  const debouncedQuery = useDebounce(query, 300);

  return useMemo(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) return [];

    const normalizedQuery = debouncedQuery.toLowerCase().trim();
    const suggestions: SuggestionItem[] = [];

    // Helper function to check if text matches query
    const matches = (text: string): boolean => {
      return text.toLowerCase().includes(normalizedQuery);
    };

    // Search in machines
    machines.forEach(machine => {
      const matchingFields: string[] = [];
      
      // Check machine name
      if (machine.machineName && matches(machine.machineName)) {
        matchingFields.push('name');
      }
      
      // Check machine number/ID
      if ((machine.maschinennummer && matches(machine.maschinennummer)) ||
          (machine.maschineBK && matches(machine.maschineBK))) {
        matchingFields.push('id');
      }
      
      // Check machine type
      if (machine.bautyp && matches(machine.bautyp)) {
        matchingFields.push('type');
      }

      if (matchingFields.length > 0) {
        const factoryHint = machine.factoryName ? ` (${machine.factoryName})` : '';
        suggestions.push({
          value: machine.machineName || machine.bautyp || machine.maschinennummer,
          type: 'machine',
          label: `${machine.machineName || machine.bautyp || machine.maschinennummer}${factoryHint}`,
          subtitle: 'Machine',
          id: machine.maschineBK || machine.maschinennummer
        });
      }
    });

    // Search in lines - only by name and OEM number
    lines.forEach(line => {
      const matchingFields: string[] = [];
      
      // Search by line name
      if (line.linieName && matches(line.linieName)) {
        matchingFields.push('name');
      }
      
      // Search by OEM number (oemNummer is on the line, not machines)
      // Also match if user types just the numeric part (e.g., "1918" matches "L1918")
      if (line.oemNummer) {
        const oemLower = line.oemNummer.toLowerCase();
        // Match if query matches OEM or if query matches numeric part of OEM
        const oemNumericPart = oemLower.replace(/^l/i, ''); // Strip leading L
        if (oemLower.includes(normalizedQuery) || oemNumericPart.includes(normalizedQuery)) {
          matchingFields.push('oem');
        }
      }
      
      if (matchingFields.length > 0) {
        // Include factory name in label to distinguish lines with same name
        const factoryHint = line.factoryName ? ` (${line.factoryName})` : '';
        const oemHint = line.oemNummer ? ` (OEM: ${line.oemNummer})` : '';
        suggestions.push({
          value: line.linieName || line.linieBK,
          type: 'line',
          label: line.linieNummer && line.linieName 
            ? `${line.linieNummer} - ${line.linieName}${factoryHint}${oemHint}` 
            : `${line.linieName || line.linieBK}${factoryHint}${oemHint}`,
          subtitle: line.oemNummer ? `OEM: ${line.oemNummer}` : `Line with ${line.maschinen?.length || 0} machines`,
          id: line.linieBK
        });
      }
    });

    // Remove duplicates by ID (not value) to allow same-named items from different locations
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
      index === self.findIndex(s => s.id === suggestion.id && s.type === suggestion.type)
    );

    // Check if query looks like an OEM number (numeric or starts with L followed by digits)
    const looksLikeOemQuery = /^l?\d+$/i.test(normalizedQuery);

    // Sort by relevance
    return uniqueSuggestions
      .sort((a, b) => {
        // Prioritize lines with OEM match when query looks like OEM number
        if (looksLikeOemQuery) {
          const aIsOemMatch = a.type === 'line' && a.subtitle?.startsWith('OEM:');
          const bIsOemMatch = b.type === 'line' && b.subtitle?.startsWith('OEM:');
          if (aIsOemMatch && !bIsOemMatch) return -1;
          if (!aIsOemMatch && bIsOemMatch) return 1;
        }
        
        const aStartsWith = a.value.toLowerCase().startsWith(normalizedQuery);
        const bStartsWith = b.value.toLowerCase().startsWith(normalizedQuery);
        
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        
        // Secondary sort by type priority (lines first when OEM query, otherwise machines first)
        const typePriority = looksLikeOemQuery 
          ? { line: 0, machine: 1 }  // OEM queries: lines first
          : { machine: 0, line: 1 }; // Other queries: machines first
        return typePriority[a.type] - typePriority[b.type];
      })
      .slice(0, maxResults);
  }, [debouncedQuery, machines, lines, maxResults]);
};
